"use client";

import { useQuery } from "@tanstack/react-query";
import type { CanvasVariant } from "../canvas/variant-resolver";
import type { Artwork, ArtworkCollection, ArtworkKind } from "./artwork-types";
import { ART_VARIANT_QUERY, artPrintVariants } from "./art-variants";

// Lives in its own module so server components (metadata) can call it too.
import { artworkImageUrl } from "./artwork-image";
export { artworkImageUrl } from "./artwork-image";

const BASE = process.env.NEXT_PUBLIC_BASE_URL ?? "";

/**
 * The same display image, for the AR wall preview, which draws it into a
 * canvas and so reads it with CORS. Its own URL on purpose: the page's <img>
 * fetched the plain one without CORS, and a browser that reused that cached
 * answer for a CORS read would refuse it (a tainted canvas, no model).
 */
export function artworkArImageUrl(slug: string, version?: string): string {
  return `${artworkImageUrl(slug, "display", version)}&ar=1`;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
  return (await res.json()) as T;
}

interface Paged<T> {
  data?: T[];
}

const unwrap = <T,>(payload: Paged<T> | T[]): T[] =>
  Array.isArray(payload) ? payload : (payload?.data ?? []);

export function useArtworks(kind: ArtworkKind = "ART") {
  return useQuery({
    queryKey: ["artworks", kind],
    queryFn: async () => {
      const payload = await getJson<Paged<Artwork> | Artwork[]>(
        `/artwork?filter=kind||$eq||${kind}&filter=active||$eq||true` +
          `&join=collections&sort=sortOrder,ASC&limit=200`,
      );
      return unwrap(payload);
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * One work by slug. `initial` is the server's read of the same row (the page
 * fetched it for its metadata); passing it lets the page render at once
 * instead of behind a spinner.
 */
export function useArtwork(slug: string | undefined, initial?: Artwork | null) {
  return useQuery({
    queryKey: ["artwork", slug],
    enabled: !!slug,
    initialData: initial ?? undefined,
    queryFn: async () => {
      const payload = await getJson<Paged<Artwork> | Artwork[]>(
        `/artwork?filter=slug||$eq||${encodeURIComponent(slug!)}` +
          `&join=collections&limit=1`,
      );
      return unwrap(payload)[0] ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useArtworkCollections() {
  return useQuery({
    queryKey: ["artworkCollections"],
    queryFn: async () => {
      const payload = await getJson<
        Paged<ArtworkCollection> | ArtworkCollection[]
      >(`/artworkCollection?filter=active||$eq||true&sort=sortOrder,ASC`);
      return unwrap(payload);
    },
    staleTime: 15 * 60 * 1000,
  });
}

/**
 * The print formats currently on sale.
 *
 * The gallery needs these to hide works nothing can print (see
 * hide-unprintable.ts). The detail page fetches the same rows through the
 * generated client because it also wants the definition's variantAxes; this
 * hook is the read-only view the gallery needs, cached alongside the artworks
 * so browsing costs one request, not one per card.
 */
export function useCanvasVariants() {
  return useQuery({
    queryKey: ["canvas-variants"],
    queryFn: async () => {
      // Canvas-product variants only: the map product's MAP- twins share the
      // table and every size (see art-variants.ts).
      const payload = await getJson<Paged<CanvasVariant> | CanvasVariant[]>(
        `/productWithMediaVariant?${ART_VARIANT_QUERY}`,
      );
      return artPrintVariants(unwrap(payload));
    },
    // Formats change a few times a year; the gallery is browsed constantly.
    staleTime: 5 * 60 * 1000,
  });
}
